import {
    getAiPricing,
    updateAiPricing,
    type AiModelPrice,
    type AiPricing,
} from '@/lib/api/master/ai-usage-api'
import { useCallback, useEffect, useState } from 'react'
import { MdAdd, MdCloudDownload, MdDelete, MdSave } from 'react-icons/md'
import { toast } from 'react-toastify'
import { OpenRouterPriceDialog } from './openrouter-price-dialog'

type PriceField = Exclude<keyof AiModelPrice, 'model'>

const PRICE_COLUMNS: Array<{ field: PriceField; label: string; hint: string }> = [
    { field: 'inputUsdPerMTok', label: 'Entrada', hint: 'US$ / 1M tokens' },
    { field: 'cachedInputUsdPerMTok', label: 'Entrada em cache', hint: 'US$ / 1M tokens' },
    { field: 'outputUsdPerMTok', label: 'Saída', hint: 'US$ / 1M tokens' },
    { field: 'audioInputUsdPerMTok', label: 'Áudio', hint: 'US$ / 1M tokens' },
    { field: 'usdPerMinute', label: 'Por minuto', hint: 'US$ / min de áudio' },
]

function emptyModel(): AiModelPrice {
    return {
        model: '',
        inputUsdPerMTok: 0,
        cachedInputUsdPerMTok: 0,
        outputUsdPerMTok: 0,
        audioInputUsdPerMTok: 0,
        usdPerMinute: 0,
    }
}

/**
 * Preço de custo de cada modelo na OpenAI. O custo de uma chamada é calculado
 * na gravação, com a tabela vigente: mudar aqui não refaz o histórico.
 */
export function AiPricingForm() {
    const [pricing, setPricing] = useState<AiPricing | null>(null)
    const [saving, setSaving] = useState(false)
    const [openRouterOpen, setOpenRouterOpen] = useState(false)
    const closeOpenRouter = useCallback(() => setOpenRouterOpen(false), [])

    useEffect(() => {
        void (async () => {
            const response = await getAiPricing()
            if (response.success && response.result) setPricing(response.result)
            else toast.error(response.error || 'Não foi possível carregar a tabela de preços')
        })()
    }, [])

    if (!pricing) {
        return (
            <div className="flex justify-center py-20">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        )
    }

    const updateModel = (index: number, patch: Partial<AiModelPrice>) => {
        setPricing({
            ...pricing,
            models: pricing.models.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        })
    }

    const applyOpenRouter = (
        updates: Array<Pick<AiModelPrice, 'model' | 'inputUsdPerMTok' | 'cachedInputUsdPerMTok' | 'outputUsdPerMTok'>>
    ) => {
        const models = [...pricing.models]
        for (const update of updates) {
            const index = models.findIndex(row => row.model === update.model)
            if (index >= 0) models[index] = { ...models[index], ...update }
            else models.push({ ...emptyModel(), ...update })
        }
        setPricing({ ...pricing, models })
        setOpenRouterOpen(false)
        toast.info(`${updates.length} preço(s) aplicado(s). Confira e clique em Salvar tabela de preços.`)
    }

    const handleSave = async () => {
        const models = pricing.models.map(row => ({ ...row, model: row.model.trim() }))
        if (models.some(row => !row.model)) {
            toast.error('Informe o nome de todos os modelos ou remova a linha vazia')
            return
        }
        const names = models.map(row => row.model)
        if (new Set(names).size !== names.length) {
            toast.error('Há modelo repetido na tabela')
            return
        }

        setSaving(true)
        const response = await updateAiPricing({ ...pricing, models })
        setSaving(false)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível salvar a tabela de preços')
            return
        }
        setPricing(response.result)
        toast.success('Tabela de preços salva. Vale para as próximas chamadas.')
    }

    return (
        <div className="space-y-6">
            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold mb-1">Cotação do dólar</h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        Usada só para mostrar o custo em reais. O custo é gravado em dólar, que é
                        como a OpenAI cobra.
                    </p>
                    <div className="form-control w-full max-w-xs">
                        <label className="label pb-1.5" htmlFor="ai-usd-brl-rate">
                            <span className="label-text font-medium">R$ por US$ 1</span>
                        </label>
                        <input
                            id="ai-usd-brl-rate"
                            type="number"
                            min={0.01}
                            step={0.01}
                            className="input input-bordered w-full"
                            value={pricing.usdBrlRate}
                            onChange={event =>
                                setPricing({ ...pricing, usdBrlRate: Number(event.target.value) })
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
                        <div>
                            <h2 className="text-lg font-semibold">Preço por modelo</h2>
                            <p className="text-sm text-base-content/60 max-w-2xl">
                                Use o botão do OpenRouter para atualizar. O modelo com data
                                (gpt-6-luna-2026-09-22) usa o preço do nome mais longo que o
                                começa. Modelo sem preço grava o custo como desconhecido e aparece
                                em aviso na aba Consumo.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline"
                                onClick={() => setOpenRouterOpen(true)}
                            >
                                <MdCloudDownload />
                                Atualizar pelo OpenRouter
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline"
                                onClick={() =>
                                    setPricing({ ...pricing, models: [...pricing.models, emptyModel()] })
                                }
                            >
                                <MdAdd />
                                Adicionar modelo
                            </button>
                        </div>
                    </div>

                    <div className="space-y-3 mt-4">
                        {pricing.models.map((row, index) => (
                            <div
                                key={index}
                                className="rounded-box border border-base-300 p-4 space-y-4"
                            >
                                <div className="flex flex-wrap items-end gap-3">
                                    <div className="form-control flex-1 min-w-56 max-w-md">
                                        <label className="label pb-1.5" htmlFor={`ai-model-${index}`}>
                                            <span className="label-text font-medium">Modelo</span>
                                        </label>
                                        <input
                                            id={`ai-model-${index}`}
                                            type="text"
                                            className="input input-bordered input-sm w-full font-mono"
                                            placeholder="gpt-6-luna"
                                            value={row.model}
                                            onChange={event =>
                                                updateModel(index, { model: event.target.value })
                                            }
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-ghost text-error ml-auto"
                                        title="Remover modelo"
                                        onClick={() =>
                                            setPricing({
                                                ...pricing,
                                                models: pricing.models.filter((_, i) => i !== index),
                                            })
                                        }
                                    >
                                        <MdDelete />
                                        Remover
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-x-4 gap-y-3">
                                    {PRICE_COLUMNS.map(column => (
                                        <div key={column.field} className="form-control min-w-0">
                                            <label
                                                className="block pb-1.5"
                                                htmlFor={`ai-model-${index}-${column.field}`}
                                            >
                                                <span className="block text-sm font-medium leading-tight">
                                                    {column.label}
                                                </span>
                                                <span className="block text-xs text-base-content/50 leading-tight mt-0.5">
                                                    {column.hint}
                                                </span>
                                            </label>
                                            <input
                                                id={`ai-model-${index}-${column.field}`}
                                                type="number"
                                                min={0}
                                                step="any"
                                                className="input input-bordered input-sm w-full tabular-nums"
                                                value={row[column.field]}
                                                onChange={event =>
                                                    updateModel(index, {
                                                        [column.field]: Number(event.target.value),
                                                    })
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {openRouterOpen && (
                <OpenRouterPriceDialog
                    formModels={pricing.models}
                    onClose={closeOpenRouter}
                    onApply={applyOpenRouter}
                />
            )}

            <div className="flex justify-end">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? <span className="loading loading-spinner loading-sm" /> : <MdSave />}
                    Salvar tabela de preços
                </button>
            </div>
        </div>
    )
}
